import React from "react";
import { Link } from "react-router-dom";
import {
  FlaskConical, ShieldCheck, Truck, FileCheck2, BadgeCheck, Microscope,
  ArrowRight, Target, Beaker,
} from "lucide-react";
import { LAB_IMAGES } from "../mock";

const About = () => {
  return (
    <div>
      {/* hero */}
      <section className="relative bg-gradient-to-b from-blue-50 to-white border-b border-slate-200">
        <div className="absolute inset-0 fesx-grid-bg opacity-60" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 text-blue-700 px-3.5 py-1.5 text-xs font-semibold">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> ABOUT FESX
          </span>
          <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl text-slate-900 leading-tight">
            Setting the standard for<br /><span className="text-blue-600">research materials</span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            FesX Peptides is an Australian research-first platform delivering documented,
            batch-level analytical results for scientific use — never marketing claims.
          </p>
        </div>
      </section>

      {/* stats */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { k: "99%+", v: "Average Purity" },
            { k: "HPLC", v: "Testing Method" },
            { k: "CoA", v: "Per Batch" },
            { k: "48+", v: "Compounds" },
          ].map((s) => (
            <div key={s.v} className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
              <div className="font-display font-extrabold text-3xl text-blue-600">{s.k}</div>
              <div className="mt-1 text-sm text-slate-500 font-medium">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* mission split */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-14">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden shadow-xl border border-slate-100">
            <img src={LAB_IMAGES.lab3} alt="FesX laboratory" className="w-full h-[420px] object-cover" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600">Our approach</span>
            <h2 className="mt-1 font-display font-extrabold text-3xl text-slate-900">Uncompromising quality, complete transparency</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Every product undergoes controlled quality testing prior to release. Defined purity standards are
              maintained across all research materials, with precision and consistency guiding our process. Unlike
              suppliers that publish a single certificate per compound, we publish independent laboratory results for
              every batch received.
            </p>
            <div className="mt-6 space-y-3">
              {[
                { icon: Microscope, t: "Third-party HPLC analysis on every batch" },
                { icon: FileCheck2, t: "Unique batch IDs with published CoAs" },
                { icon: ShieldCheck, t: "Vials match their tested CoA imagery" },
              ].map((f) => (
                <div key={f.t} className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <f.icon className="h-4.5 w-4.5" />
                  </div>
                  <span className="text-slate-700 font-medium">{f.t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* values */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600">Why FesX</span>
            <h2 className="mt-1 font-display font-extrabold text-3xl text-slate-900">Built for laboratories that need certainty</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: FlaskConical, t: "Uncompromising Quality", s: "Controlled testing before release, with defined purity standards across all materials." },
              { icon: Truck, t: "Express Shipping", s: "Same-day dispatch Mon–Fri via AusPost Express with full tracking Australia-wide." },
              { icon: BadgeCheck, t: "Secure Payments", s: "Cards, Apple Pay, Google Pay and crypto, all with 256-bit SSL encryption." },
              { icon: Target, t: "Dedicated Support", s: "Knowledgeable team for documentation and order enquiries via email and contact form." },
            ].map((v) => (
              <div key={v.t} className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="h-11 w-11 rounded-xl bg-blue-600 text-white flex items-center justify-center">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display font-bold text-slate-900">{v.t}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{v.s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* cta */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-700 px-8 py-12 text-center text-white">
          <Beaker className="h-10 w-10 mx-auto text-blue-200" />
          <h2 className="mt-3 font-display font-extrabold text-3xl">Explore the FesX research catalogue</h2>
          <p className="mt-2 text-blue-100 max-w-xl mx-auto">Detailed compound profiles and published CoAs for every peptide we stock.</p>
          <Link to="/shop" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white text-blue-700 font-bold px-6 py-3.5 hover:bg-blue-50 transition-colors">
            Browse Catalogue <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
